/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { v4 as uuidv4 } from 'uuid';
import parse from 'parse-json';
import JsonQuery from 'json-query';
import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { merge } from 'object-path-immutable';
import {
    hooks as commonHooks,
    types as commonTypes,
    utils as commonUtils,
} from '@sitebuilder/common';
import {
    getThemeBasedColorPalette,
    getColorPalette,
    getTextSchemaProperties,
    getStyleSchemaProperties,
    getLayoutSchemaProperties,
} from '../utils';
import { sampleData } from '../ChartTable/sampleData';
import ChartTable from '../ChartTable/ChartTable';
import HeaderWrapper from '../Header/HeaderWrapper';
import { Headings } from '../Header/Header';

interface highChartOptions {
    path?: string;
    value?: string;
    valueType?: string;
    functionToTransform?: string;
}

interface TableColumn {
    label?: string;
    value?: string;
    isOnlyText?: boolean;
}
interface PieChartProps {
    options?: highChartOptions[];
    dataKey?: string;
    chartTitles?: Headings[];
    columns?: TableColumn[];
    pieTableHeaderRow?: string;
    altText?: string;
    colorPallete?: string[];
    paletteId?: string;
    typographyClass?: string;
    disableTabStaticData?: boolean;
}

interface WidgetProperties extends commonTypes.WidgetPropertiesBase, PieChartProps {}

interface Widgetprops extends commonTypes.WidgetInstanceProps, PieChartProps {
    showChartTableOnly?: boolean;
}

const PieChart = (props: Widgetprops) => {
    const { documentType, colorPalettes } = commonHooks.useAppContext();
    const widgetProperties = commonHooks.useWidgetProperties<WidgetProperties>(
        props.widgetInstanceId
    );
    const { disableTabStaticData: globalDisableTabStaticData } =
        commonHooks.useWidgetProperties<WidgetProperties>('root') || {};
    const {
        styles,
        options,
        dataKey,
        chartTitles,
        pieTableHeaderRow,
        altText,
        colorPallete,
        columns,
        paletteId,
        disableTabStaticData,
    } = widgetProperties || props;
    const { data, showChartTableOnly } = props;
    const i18n = commonHooks.useLocalization();

    const pieChartData: any = dataKey ? JsonQuery(`${dataKey}`, { data }).value : sampleData;

    const sortedData = Array.isArray(pieChartData)
        ? parse(JSON.stringify(pieChartData)).sort((a: any, b: any) => a.sortOrder - b.sortOrder)
        : [];
    const processedData = sortedData?.map((a: any) => ({ name: a.holding, y: a.allocation }));

    let orderedColors =
        data?.themeSelected && paletteId
            ? getThemeBasedColorPalette(colorPalettes?.[paletteId], data?.colorSpace)
            : getColorPalette();
    // eslint-disable-next-line
    const customColorPallete: string[] = (data as any)?.colorPallete || colorPallete || [];

    if (customColorPallete.length > 0) {
        orderedColors = customColorPallete;
    }

    const pieId = uuidv4();

    let highChartOptions: Highcharts.Options = {
        chart: {
            type: 'pie',
            height: 200,
        },
        accessibility: {
            keyboardNavigation: {
                enabled: !globalDisableTabStaticData && !disableTabStaticData,
            },
        },
        title: { text: '' },
        credits: { enabled: false },
        legend: { enabled: false },
        tooltip: {
            pointFormat: '',
        },
        plotOptions: {
            pie: {
                size: '100%',
                innerSize: '40%',
                slicedOffset: 0,
                allowPointSelect: false,
                enableMouseTracking: false,
                borderWidth: 0.5,
                borderColor: '#fff',
                animation: false,
                colors: orderedColors,
                cursor: 'pointer',
                dataLabels: {
                    enabled: false,
                },
            },
        },
        series: [{ type: 'pie', data: processedData }],
    };

    Array.isArray(options) &&
        options.forEach((o) => {
            let userOptions;

            const transformFunctionName = `${props.widgetInstanceId}-transform`;

            try {
                if (!o.valueType || o.valueType === 'jsonString') {
                    userOptions = parse(o.value as string);
                } else {
                    userOptions = commonUtils.transformDataWithGlobals(
                        transformFunctionName,
                        o.functionToTransform,
                        {
                            data: { ...data, pieChartData, processedData },
                            i18n,
                        }
                    );
                }
            } catch (error) {
                userOptions = {};
            }

            orderedColors = userOptions?.pie?.colors?.length
                ? userOptions.pie.colors
                : orderedColors;
            highChartOptions = merge(highChartOptions, o.path, userOptions);
        });

    return (
        <>
            <div
                className={`pie-chart-container ${
                    widgetProperties?.typographyClass ? widgetProperties.typographyClass : ''
                }`}
                css={css`
                    ${getTextSchemaProperties(
                        widgetProperties,
                        data?.colorSpace
                    )}${getStyleSchemaProperties(widgetProperties)}${getLayoutSchemaProperties(
                        widgetProperties
                    )}${styles || ''}
                `}
            >
                <HeaderWrapper
                    headings={chartTitles}
                    data={data}
                    disableTabStaticData={globalDisableTabStaticData || disableTabStaticData}
                />

                <div id={`${pieId}`} className="js-pie-tile">
                    {!showChartTableOnly && (
                        <>
                            {['pdf', 'pptx'].includes(documentType) ? (
                                <div id={`${pieId}-pie-chart`} />
                            ) : (
                                <HighchartsReact
                                    highcharts={Highcharts}
                                    options={highChartOptions}
                                />
                            )}
                        </>
                    )}
                    {altText && <figcaption dangerouslySetInnerHTML={{ __html: altText || '' }} />}

                    <ChartTable
                        headerRow={pieTableHeaderRow}
                        columns={columns}
                        colors={JSON.stringify(orderedColors)}
                        data={data}
                        dataKey={dataKey}
                        disableTabStaticData={globalDisableTabStaticData || disableTabStaticData}
                    />
                </div>
            </div>

            {!showChartTableOnly && (
                <>
                    <script
                        id={`${pieId}-options`}
                        type="text/json"
                        dangerouslySetInnerHTML={{ __html: JSON.stringify(highChartOptions) }}
                    ></script>
                    <script
                        id={`${pieId}-json-data`}
                        type="text/json"
                        dangerouslySetInnerHTML={{ __html: JSON.stringify(processedData) }}
                    ></script>
                </>
            )}
        </>
    );
};

export default PieChart;
